import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import LocalPizzaIcon from "@mui/icons-material/LocalPizza";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import { useNavigate } from "react-router-dom";

const REDGRAD = "linear-gradient(135deg, #7A0E0E 0%, #B71C1C 50%, #E64A19 100%)";
const AMBERGRAD = "linear-gradient(135deg, #FF8F00 0%, #FFB300 50%, #FFC107 100%)";

const FEATURES = [
  { icon: <LocalFireDepartmentIcon />, title: "Wood-Fired", desc: "Stone-baked perfection in every bite." },
  { icon: <DeliveryDiningIcon />, title: "30-Min Delivery", desc: "Hot at your door, guaranteed." },
  { icon: <RestaurantMenuIcon />, title: "Fresh Daily", desc: "Locally sourced, never frozen." },
  { icon: <StarRoundedIcon />, title: "4.9 ★ Rated", desc: "Loved by 50,000+ foodies." },
];

const DISHES = [
  { name: "Margherita Supreme", price: "$12.99", tag: "Bestseller", emoji: "🍕" },
  { name: "Truffle Mushroom", price: "$16.99", tag: "Chef's Pick", emoji: "🍄" },
  { name: "Spicy Pepperoni", price: "$14.49", tag: "Hot 🔥", emoji: "🌶️" },
  { name: "BBQ Chicken Ranch", price: "$15.99", tag: "New", emoji: "🍗" },
];

const TESTIMONIALS = [
  { name: "Sarah M.", text: "Best pizza in town! The crust is unreal.", rating: 5 },
  { name: "James K.", text: "Fast delivery and always piping hot. Perfection.", rating: 5 },
  { name: "Aisha R.", text: "I order weekly. The truffle one ruined me for life.", rating: 5 },
];

const HomePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box sx={{ overflow: "hidden", background: "#FFF8F0" }}>
      {/* HERO */}
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: "92vh", md: "88vh" },
          background:
            "radial-gradient(1200px 600px at 10% 10%, rgba(255,179,0,0.18), transparent 60%), radial-gradient(900px 500px at 90% 30%, rgba(230,74,25,0.22), transparent 60%), #FFF8F0",
          display: "flex",
          alignItems: "center",
          px: { xs: 3, md: 8 },
          py: { xs: 8, md: 4 },
        }}
      >
        {/* floating blobs */}
        {[
          { top: "10%", left: "5%", size: 200, grad: REDGRAD, delay: 0 },
          { top: "60%", right: "8%", size: 260, grad: AMBERGRAD, delay: 1.2 },
        ].map((b, i) => (
          <motion.div
            key={i}
            animate={{ y: [0, -30, 0], x: [0, 20, 0] }}
            transition={{ duration: 8, delay: b.delay, repeat: Infinity, ease: "easeInOut" }}
            style={{
              position: "absolute",
              top: b.top,
              left: b.left,
              right: b.right,
              width: b.size,
              height: b.size,
              borderRadius: "50%",
              background: b.grad,
              filter: "blur(80px)",
              opacity: 0.35,
              zIndex: 0,
            }}
          />
        ))}

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                <Chip
                  label="🔥 #1 Pizza in the City"
                  sx={{
                    mb: 3,
                    px: 1.5,
                    py: 2.5,
                    fontWeight: 700,
                    background: "rgba(183,28,28,0.1)",
                    color: "#B71C1C",
                    border: "1px solid rgba(183,28,28,0.2)",
                  }}
                />
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: 44, sm: 60, md: 80 },
                    lineHeight: 1.02,
                    mb: 2,
                    fontWeight: 900,
                  }}
                >
                  Crafted with{" "}
                  <Box component="span" sx={{ background: REDGRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                    fire.
                  </Box>
                  <br />
                  Served with{" "}
                  <Box component="span" sx={{ background: AMBERGRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                    love.
                  </Box>
                </Typography>
                <Typography sx={{ fontSize: { xs: 16, md: 19 }, color: "#6b4a3a", mb: 4, maxWidth: 520 }}>
                  Hand-tossed dough, San Marzano tomatoes, fresh mozzarella — and zero shortcuts. Order in 30 seconds.
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    size="large"
                    onClick={() => navigate("/menu")}
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                      px: 4,
                      py: 1.6,
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#fff",
                      background: REDGRAD,
                      boxShadow: "0 14px 34px rgba(183,28,28,0.4)",
                      "&:hover": { boxShadow: "0 20px 44px rgba(183,28,28,0.55)", transform: "translateY(-2px)" },
                      transition: "all 0.3s",
                    }}
                  >
                    Order Now
                  </Button>
                  <Button
                    size="large"
                    onClick={() => navigate("/about")}
                    sx={{
                      px: 4,
                      py: 1.6,
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#B71C1C",
                      border: "2px solid rgba(183,28,28,0.3)",
                      "&:hover": { background: "rgba(183,28,28,0.06)", borderColor: "#B71C1C" },
                    }}
                  >
                    Our Story
                  </Button>
                </Stack>

                <Stack direction="row" spacing={4} sx={{ mt: 5 }}>
                  {[
                    { v: "50K+", l: "Happy Foodies" },
                    { v: "4.9★", l: "Avg Rating" },
                    { v: "30min", l: "Delivery" },
                  ].map((s) => (
                    <Box key={s.l}>
                      <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 900, color: "#B71C1C" }}>{s.v}</Typography>
                      <Typography sx={{ fontSize: 13, color: "#6b4a3a" }}>{s.l}</Typography>
                    </Box>
                  ))}
                </Stack>
              </motion.div>
            </Grid>

            {!isMobile && (
              <Grid item xs={12} md={5}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  style={{ position: "relative" }}
                >
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                    style={{
                      width: 460,
                      height: 460,
                      borderRadius: "50%",
                      background: REDGRAD,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 30px 80px rgba(183,28,28,0.4)",
                      margin: "0 auto",
                      position: "relative",
                    }}
                  >
                    <Box sx={{ fontSize: 240, filter: "drop-shadow(0 10px 20px rgba(0,0,0,0.3))" }}>🍕</Box>
                  </motion.div>
                  {/* floating chips */}
                  {[
                    { top: 20, right: -10, label: "🔥 Hot", delay: 0 },
                    { bottom: 60, left: -20, label: "⭐ 4.9", delay: 0.5 },
                    { top: 200, right: -40, label: "🚀 Fast", delay: 1 },
                  ].map((c, i) => (
                    <motion.div
                      key={i}
                      animate={{ y: [0, -12, 0] }}
                      transition={{ duration: 3, delay: c.delay, repeat: Infinity }}
                      style={{ position: "absolute", ...c }}
                    >
                      <Chip
                        label={c.label}
                        sx={{
                          background: "#fff",
                          fontWeight: 700,
                          fontSize: 15,
                          px: 1,
                          py: 2.5,
                          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                        }}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>

      {/* FEATURES */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack spacing={1} sx={{ textAlign: "center", mb: 6 }}>
          <Typography sx={{ color: "#B71C1C", fontWeight: 700, letterSpacing: 2, fontSize: 13 }}>WHY CHOOSE US</Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: 32, md: 48 } }}>
            Built for true food lovers
          </Typography>
        </Stack>
        <Grid container spacing={3}>
          {FEATURES.map((f, i) => (
            <Grid item xs={12} sm={6} md={3} key={f.title}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <Card
                  sx={{
                    p: 3,
                    height: "100%",
                    borderRadius: 4,
                    border: "1px solid rgba(183,28,28,0.1)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
                    transition: "all 0.3s",
                    "&:hover": { boxShadow: "0 20px 50px rgba(183,28,28,0.15)", borderColor: "rgba(183,28,28,0.3)" },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      background: REDGRAD,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      "& svg": { fontSize: 28 },
                    }}
                  >
                    {f.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 0.5 }}>{f.title}</Typography>
                  <Typography sx={{ color: "#6b4a3a", fontSize: 14 }}>{f.desc}</Typography>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* POPULAR DISHES */}
      <Box sx={{ background: "linear-gradient(180deg, #FFF8F0 0%, #FFF1E0 100%)", py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "end" }} spacing={2} sx={{ mb: 6 }}>
            <Box>
              <Typography sx={{ color: "#B71C1C", fontWeight: 700, letterSpacing: 2, fontSize: 13 }}>FAN FAVORITES</Typography>
              <Typography variant="h2" sx={{ fontSize: { xs: 32, md: 48 } }}>Popular this week</Typography>
            </Box>
            <Button
              onClick={() => navigate("/menu")}
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{ color: "#B71C1C", fontWeight: 700 }}
            >
              View Full Menu
            </Button>
          </Stack>
          <Grid container spacing={3}>
            {DISHES.map((d, i) => (
              <Grid item xs={12} sm={6} md={3} key={d.name}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -10 }}
                >
                  <Card
                    sx={{
                      borderRadius: 4,
                      overflow: "hidden",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
                      transition: "all 0.3s",
                      "&:hover": { boxShadow: "0 25px 60px rgba(183,28,28,0.2)" },
                    }}
                  >
                    <Box
                      sx={{
                        height: 180,
                        background: AMBERGRAD,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 100,
                        position: "relative",
                      }}
                    >
                      {d.emoji}
                      <Chip
                        label={d.tag}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          background: "#fff",
                          fontWeight: 700,
                          fontSize: 11,
                        }}
                      />
                    </Box>
                    <CardContent>
                      <Typography sx={{ fontWeight: 800, fontSize: 17, mb: 0.5 }}>{d.name}</Typography>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                        <Typography sx={{ fontWeight: 900, fontSize: 20, color: "#B71C1C" }}>{d.price}</Typography>
                        <Button
                          size="small"
                          onClick={() => navigate("/menu")}
                          sx={{
                            minWidth: 0,
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            background: REDGRAD,
                            color: "#fff",
                            "&:hover": { background: REDGRAD, transform: "scale(1.1)" },
                          }}
                        >
                          <AddIconLite />
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* TESTIMONIALS */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack spacing={1} sx={{ textAlign: "center", mb: 6 }}>
          <Typography sx={{ color: "#B71C1C", fontWeight: 700, letterSpacing: 2, fontSize: 13 }}>TESTIMONIALS</Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: 32, md: 48 } }}>What our foodies say</Typography>
        </Stack>
        <Grid container spacing={3}>
          {TESTIMONIALS.map((t, i) => (
            <Grid item xs={12} md={4} key={t.name}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
              >
                <Card sx={{ p: 4, borderRadius: 4, height: "100%", position: "relative", boxShadow: "0 10px 30px rgba(0,0,0,0.06)" }}>
                  <FormatQuoteRoundedIcon sx={{ fontSize: 60, color: "rgba(183,28,28,0.1)", position: "absolute", top: 10, right: 10 }} />
                  <Stack direction="row" spacing={0.5} sx={{ mb: 2 }}>
                    {Array.from({ length: t.rating }).map((_, k) => (
                      <StarRoundedIcon key={k} sx={{ color: "#FFB300", fontSize: 22 }} />
                    ))}
                  </Stack>
                  <Typography sx={{ fontSize: 16, color: "#2A1810", mb: 3, lineHeight: 1.6 }}>"{t.text}"</Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        background: REDGRAD,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                      }}
                    >
                      {t.name.charAt(0)}
                    </Box>
                    <Typography sx={{ fontWeight: 700 }}>{t.name}</Typography>
                  </Stack>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA */}
      <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
        <Card
          sx={{
            borderRadius: 6,
            p: { xs: 5, md: 8 },
            background: REDGRAD,
            color: "#fff",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 30px 80px rgba(183,28,28,0.4)",
          }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            style={{
              position: "absolute",
              top: -100,
              right: -100,
              width: 300,
              height: 300,
              borderRadius: "50%",
              background: "rgba(255,179,0,0.2)",
            }}
          />
          <Typography variant="h2" sx={{ fontSize: { xs: 32, md: 48 }, mb: 2, position: "relative" }}>
            Hungry yet? 🍕
          </Typography>
          <Typography sx={{ fontSize: { xs: 16, md: 18 }, opacity: 0.9, mb: 4, position: "relative" }}>
            Get $10 off your first order. Use code <b>FOODIE10</b>
          </Typography>
          <Button
            size="large"
            onClick={() => navigate("/menu")}
            sx={{
              px: 5,
              py: 1.7,
              fontWeight: 800,
              fontSize: 16,
              background: "#fff",
              color: "#B71C1C",
              boxShadow: "0 14px 40px rgba(0,0,0,0.25)",
              "&:hover": { background: "#FFF8F0", transform: "translateY(-2px)" },
              position: "relative",
            }}
          >
            Order Now →
          </Button>
        </Card>
      </Container>
    </Box>
  );
};

const AddIconLite = () => (
  <Box component="span" sx={{ fontSize: 22, fontWeight: 900, lineHeight: 1 }}>+</Box>
);

export default HomePage;
